import React, { useMemo, useState } from 'react';
import { DollarSign, RefreshCw } from 'lucide-react';
import { AdminEmployee } from '../types/models';

type JobCategory = 'care' | 'nurse' | 'cook' | 'other';
type EmploymentCategory = 'fulltime' | 'parttime';

const JOB_KEYS: JobCategory[] = ['care', 'nurse', 'cook', 'other'];
const JOB_LABELS: Record<JobCategory, string> = {
  care: '介護',
  nurse: '看護',
  cook: '調理',
  other: 'その他'
};

const yenFormatter = new Intl.NumberFormat('ja-JP', {
  style: 'currency',
  currency: 'JPY',
  maximumFractionDigits: 0
});

const classifyJob = (jobType: string | null | undefined): JobCategory => {
  const jt = jobType ?? '';
  if (jt.includes('介護')) return 'care';
  if (jt.includes('看護')) return 'nurse';
  if (jt.includes('調理') || jt.includes('厨房')) return 'cook';
  return 'other';
};

const isActiveAsOf = (emp: AdminEmployee, asOfDate: string): boolean => {
  if (emp.hireDate && emp.hireDate > asOfDate) return false;
  if (emp.retirementDate && emp.retirementDate <= asOfDate) return false;
  if (emp.status === 'retired' && !emp.retirementDate) return false;
  return true;
};

const countEmployeesByMatrix = (
  employees: AdminEmployee[],
  asOfDate: string
): Record<EmploymentCategory, Record<JobCategory, number>> => {
  const result = {
    fulltime: { care: 0, nurse: 0, cook: 0, other: 0 },
    parttime: { care: 0, nurse: 0, cook: 0, other: 0 }
  };
  employees.forEach(emp => {
    if (!isActiveAsOf(emp, asOfDate)) return;
    if (emp.employmentType === 'その他') return;
    const empCat: EmploymentCategory = emp.employmentType === '正社員' ? 'fulltime' : 'parttime';
    const jobCat = classifyJob(emp.jobType);
    result[empCat][jobCat] += 1;
  });
  return result;
};

const getTodayString = (): string => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

interface MatrixValues {
  fulltime: Record<JobCategory, number>;
  parttime: Record<JobCategory, number>;
}

const DEFAULT_FULLTIME_RATIOS: Record<JobCategory, number> = {
  care: 1.0,
  nurse: 1.0,
  cook: 0.7,
  other: 0.7
};

const DEFAULT_PARTTIME_RATIOS: Record<JobCategory, number> = {
  care: 0.7,
  nurse: 0.7,
  cook: 0.7,
  other: 0.7
};

const SubsidySimulatorTab: React.FC<{ employees: AdminEmployee[] }> = ({ employees }) => {
  const [baseDate, setBaseDate] = useState<string>(() => getTodayString());
  const [budget, setBudget] = useState<number>(2500000);
  const [months, setMonths] = useState<number>(6);
  const [baseAmount, setBaseAmount] = useState<number>(14000);

  const autoCounts = useMemo(
    () => countEmployeesByMatrix(employees, baseDate),
    [employees, baseDate]
  );

  const [counts, setCounts] = useState<MatrixValues>(() => ({
    fulltime: { ...autoCounts.fulltime },
    parttime: { ...autoCounts.parttime }
  }));
  const [ratios, setRatios] = useState<MatrixValues>(() => ({
    fulltime: { ...DEFAULT_FULLTIME_RATIOS },
    parttime: { ...DEFAULT_PARTTIME_RATIOS }
  }));

  const handleSyncCounts = () => {
    setCounts({
      fulltime: { ...autoCounts.fulltime },
      parttime: { ...autoCounts.parttime }
    });
  };

  const updateRatio = (emp: EmploymentCategory, job: JobCategory, value: number) => {
    setRatios(prev => ({
      ...prev,
      [emp]: { ...prev[emp], [job]: value }
    }));
  };

  const updateCount = (emp: EmploymentCategory, job: JobCategory, value: number) => {
    setCounts(prev => ({
      ...prev,
      [emp]: { ...prev[emp], [job]: value }
    }));
  };

  const calc = useMemo(() => {
    const fulltimeAmount: Record<JobCategory, number> = {
      care: baseAmount * ratios.fulltime.care,
      nurse: baseAmount * ratios.fulltime.nurse,
      cook: baseAmount * ratios.fulltime.cook,
      other: baseAmount * ratios.fulltime.other
    };
    const parttimeAmount: Record<JobCategory, number> = {
      care: fulltimeAmount.care * ratios.parttime.care,
      nurse: fulltimeAmount.nurse * ratios.parttime.nurse,
      cook: fulltimeAmount.cook * ratios.parttime.cook,
      other: fulltimeAmount.other * ratios.parttime.other
    };
    const fulltimeSubtotal = JOB_KEYS.reduce(
      (sum, k) => sum + fulltimeAmount[k] * counts.fulltime[k],
      0
    );
    const parttimeSubtotal = JOB_KEYS.reduce(
      (sum, k) => sum + parttimeAmount[k] * counts.parttime[k],
      0
    );
    const monthlyTotal = fulltimeSubtotal + parttimeSubtotal;
    const monthlyBudget = months > 0 ? budget / months : 0;
    const monthlyDiff = monthlyBudget - monthlyTotal;
    const totalOverMonths = monthlyTotal * months;
    const totalDiff = budget - totalOverMonths;
    return {
      fulltimeAmount,
      parttimeAmount,
      fulltimeSubtotal,
      parttimeSubtotal,
      monthlyTotal,
      monthlyBudget,
      monthlyDiff,
      totalOverMonths,
      totalDiff
    };
  }, [baseAmount, ratios, counts, budget, months]);

  const diffClass = (v: number): string => {
    if (v === 0) return 'text-gray-900';
    return v >= 0 ? 'text-green-700' : 'text-red-600';
  };

  return (
    <div className="space-y-6">
      {/* 基準日 + 予算入力 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <DollarSign className="w-5 h-5 text-blue-600" />
          <h3 className="text-lg font-bold text-gray-900">処遇改善給付金シミュレーター</h3>
        </div>
        <p className="text-sm text-gray-600 mb-4">
          予算合計と正社員×介護の1人あたり金額を入力し、各マスの比率・人数を調整すると月額振り分けが自動算出されます。
        </p>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">基準日（在籍判定）</label>
            <input
              type="date"
              value={baseDate}
              onChange={e => setBaseDate(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">予算合計（円）</label>
            <input
              type="number"
              value={budget}
              onChange={e => setBudget(Number(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              min={0}
              step={10000}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">支給月数</label>
            <input
              type="number"
              value={months}
              onChange={e => setMonths(Number(e.target.value) || 1)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              min={1}
              step={1}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              正社員×介護の1人あたり（円）
            </label>
            <input
              type="number"
              value={baseAmount}
              onChange={e => setBaseAmount(Number(e.target.value) || 0)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-blue-500"
              min={0}
              step={500}
            />
          </div>
        </div>
        <div className="mt-4 text-sm text-gray-600">
          月あたり予算: <span className="font-semibold text-gray-900">{yenFormatter.format(calc.monthlyBudget)}</span>
          <span className="mx-2 text-gray-400">|</span>
          {months}ヶ月で合計: <span className="font-semibold text-gray-900">{yenFormatter.format(budget)}</span>
        </div>
      </div>

      {/* 比率入力 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">比率設定</h3>
          <div className="text-xs text-gray-500">
            正社員 = 正社員×介護 × 比率 ／ パート = 該当する正社員マス × 比率
          </div>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">区分</th>
              {JOB_KEYS.map(k => (
                <th key={k} className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">
                  {JOB_LABELS[k]}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 px-3 py-2 font-medium bg-blue-50">正社員 比率</td>
              {JOB_KEYS.map(k => (
                <td key={k} className="border border-gray-300 px-2 py-1 text-center">
                  <input
                    type="number"
                    value={ratios.fulltime[k]}
                    onChange={e => updateRatio('fulltime', k, Number(e.target.value) || 0)}
                    disabled={k === 'care'}
                    className={`w-full px-2 py-1 border rounded text-center focus:outline-none focus:border-blue-500 ${
                      k === 'care' ? 'bg-gray-100 text-gray-500' : 'border-gray-300'
                    }`}
                    min={0}
                    step={0.05}
                  />
                </td>
              ))}
            </tr>
            <tr>
              <td className="border border-gray-300 px-3 py-2 font-medium bg-orange-50">パート 比率</td>
              {JOB_KEYS.map(k => (
                <td key={k} className="border border-gray-300 px-2 py-1 text-center">
                  <input
                    type="number"
                    value={ratios.parttime[k]}
                    onChange={e => updateRatio('parttime', k, Number(e.target.value) || 0)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:outline-none focus:border-blue-500"
                    min={0}
                    step={0.05}
                  />
                </td>
              ))}
            </tr>
          </tbody>
        </table>
      </div>

      {/* 人数入力 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-bold text-gray-900">人数（基準日時点）</h3>
          <button
            onClick={handleSyncCounts}
            className="flex items-center gap-2 px-3 py-2 text-sm bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            type="button"
          >
            <RefreshCw className="w-4 h-4" />
            従業員データから再集計
          </button>
        </div>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">区分</th>
              {JOB_KEYS.map(k => (
                <th key={k} className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">
                  {JOB_LABELS[k]}
                </th>
              ))}
              <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">小計</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td className="border border-gray-300 px-3 py-2 font-medium bg-blue-50">正社員 人数</td>
              {JOB_KEYS.map(k => (
                <td key={k} className="border border-gray-300 px-2 py-1 text-center">
                  <input
                    type="number"
                    value={counts.fulltime[k]}
                    onChange={e => updateCount('fulltime', k, Number(e.target.value) || 0)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:outline-none focus:border-blue-500"
                    min={0}
                    step={1}
                  />
                </td>
              ))}
              <td className="border border-gray-300 px-3 py-2 text-center font-semibold">
                {JOB_KEYS.reduce((s, k) => s + counts.fulltime[k], 0)}
              </td>
            </tr>
            <tr>
              <td className="border border-gray-300 px-3 py-2 font-medium bg-orange-50">パート 人数</td>
              {JOB_KEYS.map(k => (
                <td key={k} className="border border-gray-300 px-2 py-1 text-center">
                  <input
                    type="number"
                    value={counts.parttime[k]}
                    onChange={e => updateCount('parttime', k, Number(e.target.value) || 0)}
                    className="w-full px-2 py-1 border border-gray-300 rounded text-center focus:outline-none focus:border-blue-500"
                    min={0}
                    step={1}
                  />
                </td>
              ))}
              <td className="border border-gray-300 px-3 py-2 text-center font-semibold">
                {JOB_KEYS.reduce((s, k) => s + counts.parttime[k], 0)}
              </td>
            </tr>
          </tbody>
        </table>
        <div className="mt-3 text-xs text-gray-500">
          自動集計値 - 正社員: {JOB_KEYS.map(k => `${JOB_LABELS[k]}${autoCounts.fulltime[k]}`).join(' / ')}
          <span className="mx-2">|</span>
          パート: {JOB_KEYS.map(k => `${JOB_LABELS[k]}${autoCounts.parttime[k]}`).join(' / ')}
        </div>
      </div>

      {/* 計算結果 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <h3 className="text-lg font-bold text-gray-900 mb-4">シミュレーション結果</h3>
        <table className="w-full border-collapse">
          <thead>
            <tr className="bg-gray-50">
              <th className="border border-gray-300 px-3 py-2 text-left font-medium text-gray-700">区分</th>
              {JOB_KEYS.map(k => (
                <th key={k} className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">
                  {JOB_LABELS[k]}
                </th>
              ))}
              <th className="border border-gray-300 px-3 py-2 text-center font-medium text-gray-700">小計</th>
            </tr>
          </thead>
          <tbody>
            {/* 正社員1人あたり */}
            <tr>
              <td className="border border-gray-300 px-3 py-2 font-medium bg-blue-50">
                正社員 1人あたり
              </td>
              {JOB_KEYS.map(k => (
                <td key={k} className="border border-gray-300 px-3 py-2 text-right tabular-nums">
                  {yenFormatter.format(calc.fulltimeAmount[k])}
                </td>
              ))}
              <td className="border border-gray-300 px-3 py-2 text-right tabular-nums text-gray-400">—</td>
            </tr>
            {/* 正社員マス合計 */}
            <tr>
              <td className="border border-gray-300 px-3 py-2 font-medium bg-blue-50">
                正社員 マス合計
              </td>
              {JOB_KEYS.map(k => (
                <td key={k} className="border border-gray-300 px-3 py-2 text-right tabular-nums">
                  {yenFormatter.format(calc.fulltimeAmount[k] * counts.fulltime[k])}
                </td>
              ))}
              <td className="border border-gray-300 px-3 py-2 text-right tabular-nums font-semibold">
                {yenFormatter.format(calc.fulltimeSubtotal)}
              </td>
            </tr>
            {/* パート1人あたり */}
            <tr>
              <td className="border border-gray-300 px-3 py-2 font-medium bg-orange-50">
                パート 1人あたり
              </td>
              {JOB_KEYS.map(k => (
                <td key={k} className="border border-gray-300 px-3 py-2 text-right tabular-nums">
                  {yenFormatter.format(calc.parttimeAmount[k])}
                </td>
              ))}
              <td className="border border-gray-300 px-3 py-2 text-right tabular-nums text-gray-400">—</td>
            </tr>
            {/* パートマス合計 */}
            <tr>
              <td className="border border-gray-300 px-3 py-2 font-medium bg-orange-50">
                パート マス合計
              </td>
              {JOB_KEYS.map(k => (
                <td key={k} className="border border-gray-300 px-3 py-2 text-right tabular-nums">
                  {yenFormatter.format(calc.parttimeAmount[k] * counts.parttime[k])}
                </td>
              ))}
              <td className="border border-gray-300 px-3 py-2 text-right tabular-nums font-semibold">
                {yenFormatter.format(calc.parttimeSubtotal)}
              </td>
            </tr>
          </tbody>
          <tfoot>
            <tr className="bg-gray-100">
              <td colSpan={5} className="border border-gray-300 px-3 py-2 text-right font-bold text-gray-900">
                月額合計
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right tabular-nums font-bold text-gray-900">
                {yenFormatter.format(calc.monthlyTotal)}
              </td>
            </tr>
            <tr>
              <td colSpan={5} className="border border-gray-300 px-3 py-2 text-right font-medium text-gray-700">
                月あたり予算との差分（月）
              </td>
              <td className={`border border-gray-300 px-3 py-2 text-right tabular-nums font-bold ${diffClass(calc.monthlyDiff)}`}>
                {yenFormatter.format(calc.monthlyDiff)}
              </td>
            </tr>
            <tr>
              <td colSpan={5} className="border border-gray-300 px-3 py-2 text-right font-medium text-gray-700">
                {months}ヶ月合計
              </td>
              <td className="border border-gray-300 px-3 py-2 text-right tabular-nums font-semibold">
                {yenFormatter.format(calc.totalOverMonths)}
              </td>
            </tr>
            <tr>
              <td colSpan={5} className="border border-gray-300 px-3 py-2 text-right font-medium text-gray-700">
                予算合計との差分（累計）
              </td>
              <td className={`border border-gray-300 px-3 py-2 text-right tabular-nums font-bold ${diffClass(calc.totalDiff)}`}>
                {yenFormatter.format(calc.totalDiff)}
              </td>
            </tr>
          </tfoot>
        </table>
        <p className="mt-3 text-xs text-gray-500">
          ※ 差分がマイナス（赤）は予算超過、プラス（緑）は予算内を意味します。0円ちょうどの場合は黒表示。
        </p>
      </div>
    </div>
  );
};

export default SubsidySimulatorTab;
